import React, { createContext, useContext, useState, useEffect, useCallback, useRef } from 'react';
import { useAuth } from './AuthContext';

const ThemeContext = createContext();

export const useTheme = () => {
    const context = useContext(ThemeContext);
    if (!context) {
        throw new Error('useTheme must be used within a ThemeProvider');
    }
    return context;
};

// Apply theme variables to document - extracted for reuse
const applyThemeVariables = (isDark) => {
    if (isDark) {
        // Dark theme - deep, immersive, modern
        document.documentElement.style.setProperty('--bg-base', '#0f0f1a');
        document.documentElement.style.setProperty('--bg-deep', '#0a0a12');
        document.documentElement.style.setProperty('--bg-surface', '#1a1a2e');
        document.documentElement.style.setProperty('--bg-elevated', '#252542');
        document.documentElement.style.setProperty('--bg-card', 'rgba(26, 26, 46, 0.8)');
        document.documentElement.style.setProperty('--text-primary', '#f8fafc');
        document.documentElement.style.setProperty('--text-secondary', '#cbd5e1');
        document.documentElement.style.setProperty('--text-tertiary', '#94a3b8');
        document.documentElement.style.setProperty('--text-muted', '#64748b');
        document.documentElement.style.setProperty('--border-subtle', 'rgba(248, 250, 252, 0.06)');
        document.documentElement.style.setProperty('--border-light', 'rgba(248, 250, 252, 0.1)');
        document.documentElement.style.setProperty('--border-medium', 'rgba(248, 250, 252, 0.15)');
        document.documentElement.style.setProperty('--shadow-sm', '0 1px 2px rgba(0, 0, 0, 0.3)');
        document.documentElement.style.setProperty('--shadow-md', '0 4px 6px rgba(0, 0, 0, 0.4)');
        document.documentElement.style.setProperty('--shadow-lg', '0 10px 15px rgba(0, 0, 0, 0.5)');
        document.documentElement.style.setProperty('--glow-indigo', '0 0 30px rgba(99, 102, 241, 0.3)');
        document.documentElement.style.setProperty('--glow-cyan', '0 0 30px rgba(34, 211, 238, 0.25)');
        document.documentElement.style.setProperty('--glow-amber', '0 0 30px rgba(245, 158, 11, 0.25)');
    } else {
        // Light theme - clean, bright, professional
        document.documentElement.style.setProperty('--bg-base', '#f0f4f8');
        document.documentElement.style.setProperty('--bg-deep', '#e2e8f0');
        document.documentElement.style.setProperty('--bg-surface', '#ffffff');
        document.documentElement.style.setProperty('--bg-elevated', '#ffffff');
        document.documentElement.style.setProperty('--bg-card', 'rgba(255, 255, 255, 0.95)');
        document.documentElement.style.setProperty('--text-primary', '#1e293b');
        document.documentElement.style.setProperty('--text-secondary', '#475569');
        document.documentElement.style.setProperty('--text-tertiary', '#64748b');
        document.documentElement.style.setProperty('--text-muted', '#94a3b8');
        document.documentElement.style.setProperty('--border-subtle', 'rgba(99, 102, 241, 0.15)');
        document.documentElement.style.setProperty('--border-light', 'rgba(99, 102, 241, 0.25)');
        document.documentElement.style.setProperty('--border-medium', 'rgba(99, 102, 241, 0.35)');
        document.documentElement.style.setProperty('--shadow-sm', '0 1px 3px rgba(99, 102, 241, 0.08)');
        document.documentElement.style.setProperty('--shadow-md', '0 4px 6px rgba(99, 102, 241, 0.1)');
        document.documentElement.style.setProperty('--shadow-lg', '0 10px 25px rgba(99, 102, 241, 0.15)');
        document.documentElement.style.setProperty('--glow-indigo', '0 0 20px rgba(99, 102, 241, 0.25)');
        document.documentElement.style.setProperty('--glow-cyan', '0 0 20px rgba(34, 211, 238, 0.2)');
        document.documentElement.style.setProperty('--glow-amber', '0 0 20px rgba(245, 158, 11, 0.2)');
    }
};

// Helper to get the storage key for a user
const getThemeStorageKey = (userId) => {
    return userId ? `theme_${userId}` : 'theme_default';
};

// Helper to get saved theme for a user
const getSavedTheme = (userId) => {
    const key = getThemeStorageKey(userId);
    return localStorage.getItem(key) || 'dark';
};

// Helper to save theme for a user
const saveTheme = (userId, theme) => {
    const key = getThemeStorageKey(userId);
    localStorage.setItem(key, theme);
};

export const ThemeProvider = ({ children }) => {
    const { user } = useAuth();
    const userId = user?.id || null;
    const prevUserIdRef = useRef(userId);

    // Load initial theme - default to dark, will update when user loads
    const [theme, setTheme] = useState('dark');

    // Track if dark mode is locked (for landing/login pages)
    const darkModeLocked = useRef(false);

    // Track if this is the initial mount (to skip transition animation on first load)
    const isInitialMount = useRef(true);

    // Load user-specific theme when user changes
    useEffect(() => {
        // Skip if dark mode is locked (on landing/login)
        if (darkModeLocked.current) return;

        // User changed - load their theme preference
        if (userId !== prevUserIdRef.current) {
            prevUserIdRef.current = userId;
            const savedTheme = getSavedTheme(userId);
            setTheme(savedTheme);
        }
    }, [userId]);

    // Apply theme when it changes
    useEffect(() => {
        // If dark mode is locked, always apply dark theme regardless of state
        if (darkModeLocked.current) {
            document.documentElement.setAttribute('data-theme', 'dark');
            applyThemeVariables(true);
            return;
        }

        // Only add transition class AFTER initial mount (not on first page load)
        // This prevents the jarring animation/shake on page refresh
        if (!isInitialMount.current) {
            document.documentElement.classList.add('theme-transitioning');
        }

        // Save theme for current user
        saveTheme(userId, theme);
        document.documentElement.setAttribute('data-theme', theme);
        applyThemeVariables(theme === 'dark');

        // Remove transition class after animation completes (only if we added it)
        const timeout = setTimeout(() => {
            document.documentElement.classList.remove('theme-transitioning');
            // Mark initial mount as complete after first render settles
            isInitialMount.current = false;
        }, 300);

        return () => clearTimeout(timeout);
    }, [theme, userId]);

    const toggleTheme = useCallback(() => {
        if (darkModeLocked.current) return; // Prevent toggle when locked
        setTheme(prev => prev === 'dark' ? 'light' : 'dark');
    }, []);

    const setDarkTheme = useCallback(() => setTheme('dark'), []);
    const setLightTheme = useCallback(() => {
        if (darkModeLocked.current) return; // Prevent light mode when locked
        setTheme('light');
    }, []);

    // Lock dark mode - used by landing/login pages to force dark mode
    const lockDarkMode = useCallback(() => {
        darkModeLocked.current = true;
        document.documentElement.setAttribute('data-theme', 'dark');
        applyThemeVariables(true);
    }, []);

    // Unlock dark mode and restore user's preferred theme
    const unlockDarkMode = useCallback(() => {
        darkModeLocked.current = false;
        const savedTheme = getSavedTheme(userId);
        setTheme(savedTheme);
    }, [userId]);

    // Apply dark theme CSS variables without changing state (legacy, use lockDarkMode instead)
    const applyDarkModeStyles = useCallback(() => {
        document.documentElement.setAttribute('data-theme', 'dark');
        applyThemeVariables(true);
    }, []);

    // Restore user's saved theme (call this when entering dashboard)
    const restoreUserTheme = useCallback(() => {
        const savedTheme = getSavedTheme(userId);
        setTheme(savedTheme);
    }, [userId]);

    return (
        <ThemeContext.Provider value={{
            theme,
            setTheme,
            toggleTheme,
            setDarkTheme,
            setLightTheme,
            applyDarkModeStyles,
            restoreUserTheme,
            lockDarkMode,
            unlockDarkMode
        }}>
            {children}
        </ThemeContext.Provider>
    );
};

export default ThemeContext;
